// Route chunk prefetch helper (plan 21).
//
// After a successful login we know the dashboard (or admin) will be opened
// very shortly, so emit `<link rel="prefetch">` hints for the relevant
// lazy chunks. The chunk paths are looked up at runtime via the Vite
// manifest (`dist/.vite/manifest.json`, served as
// `/.vite/manifest.json` only if copied; in practice we resolve from
// the module paths that Vite records in `import.meta.env` at build time
// and falls back silently in dev).
//
// We never hardcode hashed filenames; if the manifest can't be fetched we
// simply noop (dev mode has no manifest and browsers won't need prefetch
// because the dev server serves source files directly).

type ManifestEntry = {
  file: string;
  css?: string[];
  imports?: string[];
  dynamicImports?: string[];
};

type Manifest = Record<string, ManifestEntry>;

let manifestCache: Manifest | null | undefined;

async function loadManifest(): Promise<Manifest | null> {
  if (manifestCache !== undefined) return manifestCache;
  if (typeof window === 'undefined') {
    manifestCache = null;
    return null;
  }
  // Vite emits the manifest under `dist/.vite/manifest.json`, which is
  // served from `<base>/.vite/manifest.json`.
  try {
    const res = await fetch('/.vite/manifest.json', { credentials: 'same-origin' });
    if (!res.ok) {
      manifestCache = null;
      return null;
    }
    const json: Manifest = await res.json();
    manifestCache = json;
    return json;
  } catch {
    manifestCache = null;
    return null;
  }
}

function injectPrefetch(href: string, as: 'script' | 'style' = 'script'): void {
  if (typeof document === 'undefined') return;
  if (document.head.querySelector(`link[rel="prefetch"][href="${href}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.as = as;
  link.href = href;
  document.head.appendChild(link);
}

/**
 * Prefetch the chunk(s) produced by a given source-file entry. The key
 * should match the one Vite records in the manifest, e.g.
 * `src/features/dashboard/DashboardPage.tsx`.
 */
export async function prefetchRouteChunk(entryKey: string): Promise<void> {
  const manifest = await loadManifest();
  if (!manifest) return;
  const entry = manifest[entryKey];
  if (!entry) return;

  injectPrefetch(`/${entry.file}`, 'script');
  entry.css?.forEach((cssFile) => injectPrefetch(`/${cssFile}`, 'style'));
  entry.imports?.forEach((imp) => {
    const importEntry = manifest[imp];
    if (importEntry) injectPrefetch(`/${importEntry.file}`, 'script');
  });
}

/**
 * Convenience: prefetch the user's post-login landing chunk. Called from
 * AuthProvider.login on success.
 */
export function prefetchPostLoginChunks(role: 'admin' | 'user'): void {
  void prefetchRouteChunk('src/features/dashboard/DashboardPage.tsx');
  if (role === 'admin') {
    void prefetchRouteChunk('src/features/admin/AdminDashboardPage.tsx');
  }
}
